import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vacancyService } from '../../services/vacancyService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import { STORAGE_BASE_URL } from '../../config/api';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';
import notificationService from '../../services/notificationService';

const VacancyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [vacancy, setVacancy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaved, setIsSaved] = useState(false);
    const [activeTab, setActiveTab] = useState('description'); // 'description' or 'company'
    const [showFullCompanyDescription, setShowFullCompanyDescription] = useState(false);
    const [showFullRecruitmentBio, setShowFullRecruitmentBio] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    useEffect(() => {
        loadVacancy();
        checkIfSaved();
    }, [id]);

    useEffect(() => {
        loadUnreadCount();
    }, []);

    const loadUnreadCount = async () => {
        try {
            const response = await notificationService.getUnreadCount();
            if (response.success) {
                setUnreadCount(response.count);
            }
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const loadVacancy = async () => {
        try {
            setLoading(true);
            const response = await vacancyService.getById(id);
            if (response.success) {
                setVacancy(response.data);
            }
        } catch (error) {
            toast.error(t('employee.vacancyDetail.loadFailed'));
            navigate('/employee/vacancies');
        } finally {
            setLoading(false);
        }
    };

    const checkIfSaved = async () => {
        try {
            const response = await vacancyService.getSaved();
            if (response.success) {
                const savedIds = response.data.map(v => v.id);
                setIsSaved(savedIds.includes(parseInt(id)));
            }
        } catch (error) {
            console.error('Failed to check saved status');
        }
    };

    const handleSave = async () => {
        try {
            await vacancyService.save(id);
            setIsSaved(true);
            toast.success(t('employee.vacancyDetail.saveSuccess'));
        } catch (error) {
            toast.error(t('employee.vacancyDetail.saveFailed'));
        }
    };

    const handleUnsave = async () => {
        try {
            await vacancyService.unsave(id);
            setIsSaved(false);
            toast.success(t('employee.vacancyDetail.unsaveSuccess'));
        } catch (error) {
            toast.error(t('employee.vacancyDetail.unsaveFailed'));
        }
    };

    const handleApply = () => {
        navigate(`/employee/vacancies/${id}/apply`);
    };

    const formatLocation = (location) => {
        if (!location) return t('employee.vacancyDetail.defaultLocation');
        if (typeof location === 'string') return location;
        if (typeof location === 'object' && (location.lat || location.long)) {
            return t('employee.vacancyDetail.defaultLocation');
        }
        return t('employee.vacancyDetail.defaultLocation');
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return t('employee.vacancyDetail.notSpecified');
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return t('employee.vacancyDetail.timeAgo.today');
        if (diffDays === 1) return t('employee.vacancyDetail.timeAgo.yesterday');
        if (diffDays < 7) return t('employee.vacancyDetail.timeAgo.daysAgo').replace('{days}', diffDays);
        if (diffDays < 30) return t('employee.vacancyDetail.timeAgo.weeksAgo').replace('{weeks}', Math.floor(diffDays / 7));
        if (diffDays < 365) return t('employee.vacancyDetail.timeAgo.monthsAgo').replace('{months}', Math.floor(diffDays / 30));
        return t('employee.vacancyDetail.timeAgo.yearsAgo').replace('{years}', Math.floor(diffDays / 365));
    };

    const formatWorkType = (type) => {
        const types = {
            'full_time': t('employee.home.workType.fullTime'),
            'part_time': t('employee.home.workType.partTime'),
            'contract': t('employee.home.workType.contract'),
            'freelance': t('employee.home.workType.freelance')
        };
        return types[type] || type;
    };

    const formatPresenceType = (type) => {
        const types = {
            'remote': t('employee.home.presenceType.remote'),
            'hybrid': t('employee.home.presenceType.hybrid'),
            'onsite': t('employee.home.presenceType.onsite')
        };
        return types[type] || type;
    };

    const formatSalary = (salary, currency) => {
        if (!salary) return t('employee.vacancyDetail.notSpecified');
        return `${salary} ${currency || ''}`;
    };

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    if (!vacancy) {
        return <div className="text-center py-4" dir={locale === 'ar' ? 'rtl' : 'ltr'}>{t('employee.vacancyDetail.vacancyNotFound')}</div>;
    }

    const targetCompany = vacancy.target_company || vacancy.company_details?.target_company || vacancy.company;
    const companyName = vacancy.target_company_name || targetCompany?.name || t('employee.vacancyDetail.companyNamePlaceholder');
    const companyLogo = targetCompany?.logo || targetCompany?.image;
    const companyDescription = targetCompany?.description || '';

    // Split description into short and full versions
    const shortDescription = companyDescription && companyDescription.length > 150
        ? companyDescription.substring(0, 150) + '...'
        : companyDescription;

    // Get recruitment company details if posted by recruitment company
    const isPostedByRecruitment = vacancy.is_posted_by_recruitment || vacancy.is_posted_by_recruitment_company || vacancy.recruitment_company_id;
    const postingCompany = vacancy.posting_company || vacancy.recruitment_company || vacancy.company_details?.posting_company;
    const recruitmentCompanyName = postingCompany?.name;
    const recruitmentCompanyLogo = postingCompany?.image;
    const recruitmentCompanyBio = postingCompany?.bio || '';
    const recruitmentCompanyAddress = postingCompany?.address;
    const recruitmentCompanyEmail = postingCompany?.email;
    const recruitmentCompanySiteLink = postingCompany?.site_link;

    // Split recruitment company bio into short and full versions
    const shortRecruitmentBio = recruitmentCompanyBio && recruitmentCompanyBio.length > 150
        ? recruitmentCompanyBio.substring(0, 150) + '...'
        : recruitmentCompanyBio;

    return (
        <div className="vacancy-detail-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            {/* Header with Navigation */}
            <div className="settings-header d-flex justify-content-between align-items-center px-3 py-3" dir='ltr'>
                <div className="notification-icon">
                    <button
                        onClick={() => navigate('/employee/notifications')}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            position: 'relative'
                        }}
                    >
                        <span className="bell-icon">🔔</span>
                        {unreadCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '-2px',
                                    right: '-2px',
                                    background: '#dc2626',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold'
                                }}
                            >
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>
                </div>
                <div className="settings-title">
                    {vacancy.job_title?.name}
                </div>
                <button className="settings-back-btn" onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </button>
            </div>

            {/* Job Header Section */}
            <div className="job-header-section px-3 mb-3">
                <div className="d-flex align-items-start gap-3 mb-3">
                    <button
                        className="bookmark-btn-detail"
                        onClick={isSaved ? handleUnsave : handleSave}
                    >
                        {isSaved ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                            </svg>
                        )}
                    </button>
                    <div className="flex-grow-1">
                        <h1 className="job-title-detail mb-2">
                            {vacancy.job_title?.name || t('employee.vacancyDetail.jobTitlePlaceholder')}
                        </h1>
                    </div>
                    <div className="company-logo-detail">
                        {companyLogo ? (
                            <img
                                src={companyLogo.startsWith('http') ? companyLogo : `${STORAGE_BASE_URL}/${companyLogo}`}
                                alt={companyName}
                                className="logo-img-detail"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div className="company-logo-text-detail" style={{ display: companyLogo ? 'none' : 'flex' }}>
                            <b>{companyName.charAt(0).toUpperCase()}</b>
                        </div>
                    </div>
                </div>

                {/* Tags */}
                <div className="job-tags-detail d-flex gap-2 mb-3">
                    <span className="job-tag-detail">{companyName}</span>
                    <span className="job-tag-detail">{formatLocation(vacancy.location)} - {t('employee.vacancyDetail.egypt')}</span>
                    <span className="job-tag-detail">{formatTimeAgo(vacancy.created_at)}</span>
                </div>

                {/* Location Map */}
                {vacancy.has_location && vacancy.location_lat && vacancy.location_long && (
                    <div className="location-map mb-3 mt-3">
                        <a
                            href={vacancy.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${vacancy.location_lat},${vacancy.location_long}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="map-link"
                        >
                            <div className="map-placeholder">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                <span>{t('employee.vacancyDetail.viewOnMap')}</span>
                            </div>
                        </a>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="detail-tabs-container px-3">
                <div className="detail-tabs d-flex gap-2">
                    <button
                        className={`detail-tab ${activeTab === 'description' ? 'active' : ''}`}
                        onClick={() => setActiveTab('description')}
                    >
                        {t('employee.vacancyDetail.descriptionTab')}
                    </button>
                    <button
                        className={`detail-tab ${activeTab === 'company' ? 'active' : ''}`}
                        onClick={() => setActiveTab('company')}
                    >
                        {t('employee.vacancyDetail.companyTab')}
                    </button>
                </div>
            </div>

            {/* Content Area */}
            <div className="detail-content px-3 mb-5">
                {activeTab === 'company' ? (
                    <div className="company-tab-content">
                        {/* Target Company Section (Company the vacancy is for) */}
                        <section className="content-section mb-4">
                            <h3 className="section-title-detail mb-2">{t('employee.vacancyDetail.aboutCompany')}</h3>
                            {isPostedByRecruitment && (
                                <p className="section-text mb-3" style={{ color: '#6b7280', fontSize: '0.9rem', fontStyle: 'italic' }}>
                                    { companyName }
                                </p>
                            )}
                            {companyDescription && (
                                <>
                                    <p className="section-text">
                                        {showFullCompanyDescription ? companyDescription : shortDescription}
                                    </p>
                                    {companyDescription && companyDescription.length > 150 && (
                                        <button
                                            className="read-more-btn"
                                            onClick={() => setShowFullCompanyDescription(!showFullCompanyDescription)}
                                        >
                                            {showFullCompanyDescription ? t('employee.vacancyDetail.readLess') : t('employee.vacancyDetail.readMore')}
                                        </button>
                                    )}
                                </>
                            )}
                            {/* company address */}
                            {targetCompany?.address && (
                                <div className="mt-3 company-address">
                                    <h4 className="section-title-detail mb-3">{t('employee.vacancyDetail.companyAddress')}</h4>
                                    <p className="section-text">
                                        {targetCompany.address}
                                    </p>
                                </div>
                            )}
                            {/* company phone */}
                            {targetCompany?.phone && (
                                <div className="mt-3 company-phone">
                                    <h4 className="section-title-detail mb-3">{t('employee.vacancyDetail.companyPhone')}</h4>
                                    <p className="section-text">
                                        {targetCompany.phone}
                                    </p>
                                </div>
                            )}
                            {/* company email */}
                            {targetCompany?.email && (
                                <div className="mt-3 company-email">
                                    <h4 className="section-title-detail mb-3">{t('employee.vacancyDetail.companyEmail')}</h4>
                                    <p className="section-text">
                                        {targetCompany.email}
                                    </p>
                                </div>
                            )}
                            {/* company site link */}
                            {targetCompany?.site_link && (
                                <div className="mt-3 company-site-link">
                                    <h4 className="section-title-detail mb-3">{t('employee.vacancyDetail.companyWebsite')}</h4>
                                    <p className="section-text">
                                        {targetCompany.site_link}
                                    </p>
                                </div>
                            )}
                            {/* company employees count */}
                            {targetCompany?.employees_count && (
                                <div className="mt-3 company-employees-count">
                                    <h4 className="section-title-detail mb-3">{t('employee.vacancyDetail.companyEmployeesCount')}</h4>
                                    <p className="section-text">
                                        {targetCompany.employees_count}
                                    </p>
                                </div>
                            )}
                        </section>

                        {/* Recruitment Company Section (If posted by recruitment company) */}
                        {isPostedByRecruitment && postingCompany && (
                            <section className="content-section mb-4" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                                <h3 className="section-title-detail mb-2">{t('employee.vacancyDetail.aboutRecruitmentCompany') || t('employee.vacancyDetail.recruitmentCompany') || 'شركة التوظيف'}</h3>
                                <p className="section-text mb-3" style={{ color: '#6b7280', fontSize: '0.9rem', fontStyle: 'italic' }}>
                                    {t('employee.vacancyDetail.recruitmentCompanyDescription') || 'معلومات عن شركة التوظيف التي نشرت هذه الوظيفة'}
                                </p>
                                {recruitmentCompanyBio && (
                                    <>
                                        <p className="section-text">
                                            {showFullRecruitmentBio ? recruitmentCompanyBio : shortRecruitmentBio}
                                        </p>
                                        {recruitmentCompanyBio && recruitmentCompanyBio.length > 150 && (
                                            <button
                                                className="read-more-btn"
                                                onClick={() => setShowFullRecruitmentBio(!showFullRecruitmentBio)}
                                            >
                                                {showFullRecruitmentBio ? t('employee.vacancyDetail.readLess') : t('employee.vacancyDetail.readMore')}
                                            </button>
                                        )}
                                    </>
                                )}
                                {/* recruitment company address */}
                                {recruitmentCompanyAddress && (
                                    <div className="mt-3 recruitment-company-address">
                                        <h4 className="section-title-detail mb-3">{t('employee.vacancyDetail.companyAddress')}</h4>
                                        <p className="section-text">
                                            {recruitmentCompanyAddress}
                                        </p>
                                    </div>
                                )}
                                {/* recruitment company email */}
                                {recruitmentCompanyEmail && (
                                    <div className="mt-3 recruitment-company-email">
                                        <h4 className="section-title-detail mb-3">{t('employee.vacancyDetail.companyEmail')}</h4>
                                        <p className="section-text">
                                            {recruitmentCompanyEmail}
                                        </p>
                                    </div>
                                )}
                                {/* recruitment company site link */}
                                {recruitmentCompanySiteLink && (
                                    <div className="mt-3 recruitment-company-site-link">
                                        <h4 className="section-title-detail mb-3">{t('employee.vacancyDetail.companyWebsite')}</h4>
                                        <p className="section-text">
                                            <a href={recruitmentCompanySiteLink.startsWith('http') ? recruitmentCompanySiteLink : `https://${recruitmentCompanySiteLink}`} target="_blank" rel="noopener noreferrer" style={{ color: '#4a7bc4', textDecoration: 'none' }}>
                                                {recruitmentCompanySiteLink}
                                            </a>
                                        </p>
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                ) : (
                    <div className="description-tab-content">
                        {/* Job Information */}
                        <section className="content-section mb-4">
                            <h3 className="section-title-detail mb-3">{t('employee.vacancyDetail.jobInformation')}</h3>
                            <ul className="detail-list">
                                <li>{t('employee.vacancyDetail.jobTitle')} : {vacancy.job_title?.name || t('employee.vacancyDetail.notSpecified')}</li>
                                <li>{t('employee.vacancyDetail.academicDegree')} : {vacancy.academic_degree || t('employee.vacancyDetail.notSpecified')}</li>
                                <li>{t('employee.vacancyDetail.experience')} : {vacancy.experience ? `${vacancy.experience} ${t('employee.vacancyDetail.years')}` : t('employee.vacancyDetail.notSpecified')}</li>
                                <li>{t('employee.vacancyDetail.workType')} : {formatWorkType(vacancy.type_of_work)}</li>
                            </ul>
                        </section>

                        {/* Skills Section */}
                        {vacancy.skills && vacancy.skills.length > 0 && (
                            <section className="content-section mb-4">
                                <h3 className="section-title-detail mb-3">{t('employee.vacancyDetail.skills') || t('employee.profile.skills')}</h3>
                                <div className="d-flex flex-wrap gap-2">
                                    {vacancy.skills.map((skill) => (
                                        <span
                                            key={skill.id}
                                            className="status-badge status-badge-accepted"
                                            style={{
                                                padding: '0.5rem 1rem',
                                                borderRadius: '0.5rem',
                                                fontSize: '0.9rem',
                                                fontWeight: '500'
                                            }}
                                        >
                                            {skill.name}
                                        </span>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Requirements */}
                        {vacancy.description && (
                            <section className="content-section mb-4">
                                <h3 className="section-title-detail mb-3">{t('employee.vacancyDetail.requirements')}</h3>
                                <ul className="detail-list">
                                    {vacancy.description.split('\n').filter(line => line.trim()).map((req, index) => (
                                        <li key={index}>{req.trim()}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Benefits and Facilities */}
                        {vacancy.privileges && (
                            <section className="content-section mb-4">
                                <h3 className="section-title-detail mb-3">{t('employee.vacancyDetail.benefits')}</h3>
                                <ul className="detail-list">
                                    {vacancy.privileges.split('\n').filter(line => line.trim()).map((benefit, index) => (
                                        <li key={index}>{benefit.trim()}</li>
                                    ))}
                                </ul>
                            </section>
                        )}
                    </div>
                )}
            </div>

            {/* Apply Now Button */}
            {vacancy.status === 'open' && (
                <div className="apply-button-container">
                    <button className={`btn-apply-now btn-apply ${vacancy.is_applied ? 'btn-applied' : ''}`} onClick={handleApply} disabled={vacancy.is_applied}>
                        {vacancy.is_applied ? t('employee.vacancyDetail.applied') : t('employee.vacancyDetail.apply')}
                    </button>
                </div>
            )}

            {/* Bottom Navigation */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default VacancyDetail;
